/**
 * Excel Utility Functions for Heatwave Reports
 * Handles data transformation specific to Heatwave disaster reports
 */

/**
 * Format date to DD-MM-YYYY (reused from cold wave)
 */
export const formatDate = (date) => {
  const formattedDate = new Date(date);
  const day = String(formattedDate.getDate()).padStart(2, "0");
  const month = String(formattedDate.getMonth() + 1).padStart(2, "0");
  const year = formattedDate.getFullYear();
  return `${day}-${month}-${year}`;
};

/**
 * Get current timestamp in IST format for filename
 */
export const getTimestamp = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() + 330);
  const istDate = now.toISOString().split("T")[0];
  const istTime = now
    .toISOString()
    .split("T")[1]
    .split(".")[0]
    .replace(/:/g, "");
  return `${istDate}_${istTime}`;
};

/**
 * Format date for MIS Excel (handles epoch milliseconds)
 */
export const formatDateForMISExcel = (dateValue) => {
  if (!dateValue) return "";
  try {
    let date;
    if (typeof dateValue === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
      }
      date = new Date(dateValue);
    } else if (typeof dateValue === 'number' && dateValue > 10000000000) {
      date = new Date(dateValue);
    } else if (typeof dateValue === 'number') {
      date = new Date(dateValue * 1000);
    }

    if (!date || isNaN(date.getTime())) {
      return "";
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${day}-${month}-${year}`;
  } catch (e) {
    console.error('Date formatting error:', e);
    return String(dateValue);
  }
};

/**
 * Transform backend data to Excel format for Daily Heatwave Reports
 */
export const transformDataForExcel = (data, columnOrder) => {
  if (!data || data.length === 0) return { columns: [], transformedData: [] };

  const columnMapping = {
    dist_name: "Name",
    total_block_count: "Total Blocks",
    report_submitted: "Report Submitted",
    report_not_submitted: "Report Not Submitted",
  };

  const columns = columnOrder.map((key) => ({
    label: columnMapping[key] || key.toUpperCase().replace(/_/g, " "),
    key: key,
  }));

  const transformedData = data.map((row) => {
    const transformedRow = {};
    columns.forEach((col) => {
      const value = row[col.key];
      transformedRow[col.label] = value !== null && value !== undefined ? value : "";
    });
    return transformedRow;
  });

  return { columns, transformedData };
};

/**
 * Transform MIS District Datewise Heatwave Report data for Excel
 */
export const transformMISDataForExcel = (rawData) => {
  console.log('🔍 transformMISDataForExcel (Heatwave) called');
  console.log('- Input rawData type:', typeof rawData);
  
  if (!rawData || typeof rawData !== 'object') {
    console.error('❌ Invalid heatwave data');
    return { columns: [], transformedData: [] };
  }

  let dailyRecords, cumCumRecords;
  
  if (Array.isArray(rawData)) {
    dailyRecords = rawData;
    cumCumRecords = [];
  } else if (rawData.mis_data && rawData.cumulative_data) {
    dailyRecords = rawData.mis_data.heatwave || rawData.mis_data.dr_heat_wave || [];
    cumCumRecords = rawData.cumulative_data.heatwave_cum || rawData.cumulative_data.dr_heat_wave_cum || [];
  } else if (rawData.dr_heat_wave) {
    dailyRecords = rawData.dr_heat_wave;
    cumCumRecords = rawData.dr_heat_wave_cum || [];
  } else if (rawData.heatwave) {
    dailyRecords = rawData.heatwave;
    cumCumRecords = rawData.heatwave_cum || [];
  } else {
    console.error('Unknown heatwave data structure!');
    return { columns: [], transformedData: [] };
  }

  console.log(`- Daily records: ${dailyRecords?.length || 0}`);
  console.log(`- Cumulative records: ${cumCumRecords?.length || 0}`);
  
  if (!dailyRecords || dailyRecords.length === 0) {
    console.error('❌ No daily heatwave records found');
    return { columns: [], transformedData: [] };
  }

  // ✅ CREATE CUMULATIVE LOOKUP MAP (by region_id + reported_on)
  const cumMap = {};
  if (cumCumRecords && cumCumRecords.length > 0) {
    cumCumRecords.forEach(cumRec => {
      const key = `${cumRec.region_id}_${cumRec.reported_on}`;
      cumMap[key] = cumRec;
    });
  }
  console.log(`✅ Built cumulative map with ${Object.keys(cumMap).length} entries`);

  // Filter out TOTAL rows
  const filteredDailyRecords = dailyRecords.filter(row => {
    const distName = (row.dist_name || row.district_name || '').toString().trim().toUpperCase();
    const blockName = (row.block_name || '').toString().trim().toUpperCase();
    return distName !== 'TOTAL' && blockName !== 'TOTAL';
  });

  console.log(`✅ Processing ${filteredDailyRecords.length} data rows`);

  // ✅ FIXED: Include Date in field mapping
  const fieldMapping = {
    'dist_name': 'District Name',
    'reported_on': 'Date',  // ✅ ADDED
    'block_name': 'Block Name',
    'no_of_deaths_today': 'Number of Deaths due to heatwave (Today)',
    'no_of_deaths_cum': 'Number of Deaths (Cumulative)',
    'no_of_dec_ex_gratia_pay_done_today': 'Number of deceased person for whom ex-gratia payment done (Today)',
    'no_of_dec_ex_gratia_pay_done_cum': 'Number of deceased person for whom ex-gratia payment done (Cumulative)',
    'no_of_persons_hospitalized_today': 'Number of persons hospitalized (Today)',
    'no_of_persons_hospitalized_cum': 'Number of persons hospitalized (Cumulative)',
    'no_of_persons_hospitalized_pay_done_today': 'Number of hospitalized person for whom payment done (Today)',
    'no_of_persons_hospitalized_pay_done_cum': 'Number of hospitalized person for whom payment done (Cumulative)',
    'remarks': 'Remarks'
  };

  // ✅ FIXED: Include Date in column labels
  const excelColumnLabels = [
    "District Name",
    "Date",  // ✅ ADDED
    "Block Name",
    "Number of Deaths due to heatwave (Today)",
    "Number of Deaths (Cumulative)",
    "Number of deceased person for whom ex-gratia payment done (Today)",
    "Number of deceased person for whom ex-gratia payment done (Cumulative)",
    "Number of persons hospitalized (Today)",
    "Number of persons hospitalized (Cumulative)",
    "Number of hospitalized person for whom payment done (Today)",
    "Number of hospitalized person for whom payment done (Cumulative)",
    "Remarks"
  ];

  const columns = excelColumnLabels.map(label => ({ label, key: label }));

  // ✅ FIXED: Merge cumulative data with daily records
  const transformedData = filteredDailyRecords.map((dailyRec) => {
    const transformedRow = {};
    
    // ✅ Get matching cumulative record
    const cumKey = `${dailyRec.region_id}_${dailyRec.reported_on}`;
    const cumRec = cumMap[cumKey] || {};
    
    Object.keys(fieldMapping).forEach(apiField => {
      const displayName = fieldMapping[apiField];
      
      if (apiField === 'reported_on') {
        transformedRow[displayName] = formatDateForMISExcel(dailyRec[apiField]);
      } else if (apiField.endsWith('_cum')) {
        // ✅ Get cumulative value from cumRec, not dailyRec
        let value = cumRec[apiField];
        transformedRow[displayName] = (value !== null && value !== undefined && value !== 0) ? value : "-";
      } else if (apiField === 'remarks') {
        transformedRow[displayName] = dailyRec[apiField] || 'NA';
      } else {
        transformedRow[displayName] = dailyRec[apiField] !== undefined ? dailyRec[apiField] : '';
      }
    });
    
    return transformedRow;
  });

  // Sort by date
  transformedData.sort((a, b) => {
    const dateA = a['Date'] || '';
    const dateB = b['Date'] || '';
    return dateA.localeCompare(dateB);
  });

  // Add TOTAL row
  if (transformedData.length > 0) {
    const totalRow = {};
    totalRow['District Name'] = 'TOTAL';
    totalRow['Date'] = 'NA';
    totalRow['Block Name'] = 'NA';

    excelColumnLabels.forEach((columnLabel) => {
      if (columnLabel === 'District Name' || columnLabel === 'Date' || columnLabel === 'Block Name' || columnLabel === 'Remarks') {
        if (!totalRow[columnLabel]) totalRow[columnLabel] = 'NA';
        return;
      }

      let sum = 0;
      let hasValidData = false;
      transformedData.forEach(row => {
        const value = row[columnLabel];
        if (value !== null && value !== undefined && value !== '-' && value !== '' && value !== 'NA' && !isNaN(value)) {
          sum += parseFloat(value);
          hasValidData = true;
        }
      });
      totalRow[columnLabel] = hasValidData ? sum : 'NA';
    });

    totalRow['Remarks'] = 'NA';
    transformedData.push(totalRow);
  }

  console.log(`✅ Transformed ${transformedData.length} heatwave rows for Excel`);
  return { columns, transformedData };
};


/**
 * Transform MIS District Heatwave Report (aggregated by district)
 */
export const transformMISDistrictData = (rawData) => {
  console.log('🔥 transformMISDistrictData (Heatwave) called');
  
  // ✅ HANDLE NESTED STRUCTURE
  let dataArray = rawData;
  if (rawData && rawData.mis_data) {
    dataArray = rawData.mis_data.heatwave || rawData.mis_data.dr_heat_wave || [];
  }
  
  if (!dataArray || !Array.isArray(dataArray) || dataArray.length === 0) {
    console.error('❌ Invalid heatwave district data');
    return { columns: [], transformedData: [] };
  }
  
  // Continue with existing filtering logic...
  const filteredData = dataArray.filter(row => {
    const distName = (row.dist_name || row.district_name || '').toString().trim().toUpperCase();
    const blockName = (row.block_name || '').toString().trim().toUpperCase();
    return distName !== 'TOTAL' && blockName !== 'TOTAL';
  });

  const blockMap = {};

  filteredData.forEach(record => {
    const blockKey = `${record.dist_name}_${record.block_name}`;
    if (!blockMap[blockKey]) {
      blockMap[blockKey] = {
        dist_name: record.dist_name,
        block_name: record.block_name,
        no_of_deaths_today: 0,
        no_of_deaths_cum: 0,
        no_of_dec_ex_gratia_pay_done_today: 0,
        no_of_dec_ex_gratia_pay_done_cum: 0,
        no_of_persons_hospitalized_today: 0,
        no_of_persons_hospitalized_cum: 0,
        no_of_persons_hospitalized_pay_done_today: 0,
        no_of_persons_hospitalized_pay_done_cum: 0,
        remarks: record.remarks || 'NA'
      };
    }

    const block = blockMap[blockKey];

    // Today values - SUM
    block.no_of_deaths_today += (record.no_of_deaths_today || 0);
    block.no_of_dec_ex_gratia_pay_done_today += (record.no_of_dec_ex_gratia_pay_done_today || 0);
    block.no_of_persons_hospitalized_today += (record.no_of_persons_hospitalized_today || 0);
    block.no_of_persons_hospitalized_pay_done_today += (record.no_of_persons_hospitalized_pay_done_today || 0);

    // Cum values - MAX
    block.no_of_deaths_cum = Math.max(block.no_of_deaths_cum, record.no_of_deaths_cum || 0);
    block.no_of_dec_ex_gratia_pay_done_cum = Math.max(block.no_of_dec_ex_gratia_pay_done_cum, record.no_of_dec_ex_gratia_pay_done_cum || 0);
    block.no_of_persons_hospitalized_cum = Math.max(block.no_of_persons_hospitalized_cum, record.no_of_persons_hospitalized_cum || 0);
    block.no_of_persons_hospitalized_pay_done_cum = Math.max(block.no_of_persons_hospitalized_pay_done_cum, record.no_of_persons_hospitalized_pay_done_cum || 0);
  });

  const aggregatedBlocks = Object.values(blockMap);
  console.log(`✅ Aggregated into ${aggregatedBlocks.length} heatwave blocks`);

const fieldMapping = {
  'dist_name': 'District Name',
  'block_name': 'Block Name',
  'no_of_deaths_today': 'Number of Deaths due to heatwave (Today)',
  'no_of_deaths_cum': 'Number of Deaths (Cumulative)',
  'no_of_dec_ex_gratia_pay_done_today': 'Number of deceased person for whom ex-gratia payment done (Today)',
  'no_of_dec_ex_gratia_pay_done_cum': 'Number of deceased person for whom ex-gratia payment done (Cumulative)',
  'no_of_persons_hospitalized_today': 'Number of persons hospitalized (Today)',
  'no_of_persons_hospitalized_cum': 'Number of persons hospitalized (Cumulative)',
  'no_of_persons_hospitalized_pay_done_today': 'Number of hospitalized person for whom payment done (Today)',
  'no_of_persons_hospitalized_pay_done_cum': 'Number of hospitalized person for whom payment done (Cumulative)',
  'remarks': 'Remarks'
};

const excelColumnLabels = [
  "District Name",
  "Block Name",
  "Number of Deaths due to heatwave (Today)",
  "Number of Deaths (Cumulative)",
  "Number of deceased person for whom ex-gratia payment done (Today)",
  "Number of deceased person for whom ex-gratia payment done (Cumulative)",
  "Number of persons hospitalized (Today)",
  "Number of persons hospitalized (Cumulative)",
  "Number of hospitalized person for whom payment done (Today)",
  "Number of hospitalized person for whom payment done (Cumulative)",
  "Remarks"
];


  const columns = excelColumnLabels.map(label => ({ label, key: label }));

  const transformedData = aggregatedBlocks.map((row) => {
    const transformedRow = {};
    Object.keys(fieldMapping).forEach(apiField => {
      const displayName = fieldMapping[apiField];
      const value = row[apiField];

      if (apiField.endsWith('_cum')) {
        transformedRow[displayName] = (value !== null && value !== undefined && value !== 0) ? value : '-';
      } else if (apiField === 'dist_name' || apiField === 'block_name') {
        transformedRow[displayName] = value || '';
      } else {
        transformedRow[displayName] = value;
      }
    });
    return transformedRow;
  });

  // Add TOTAL row
  const totalRow = {
    'District Name': 'TOTAL',
    'Block Name': 'NA'
  };

  excelColumnLabels.forEach((columnLabel, idx) => {
    if (idx <= 1) return;

    let sum = 0;
    let validCount = 0;

    transformedData.forEach(row => {
      const value = row[columnLabel];
      if (value !== null && value !== undefined && value !== 'NA' && value !== '-' && value !== '' && !isNaN(value)) {
        sum += parseFloat(value);
        validCount++;
      }
    });

    totalRow[columnLabel] = validCount > 0 ? sum : 'NA';
  });

  transformedData.push(totalRow);

  return { columns, transformedData };
};

/**
 * Transform MIS State Heatwave Report (aggregated district data)
 */
export const transformMISStateData = (rawData) => {
  console.log('🔍 transformMISStateData (Heatwave) called');
  
  // ✅ HANDLE NESTED STRUCTURE - Extract array from object
  let dataArray = rawData;
  if (rawData && rawData.mis_data) {
    dataArray = rawData.mis_data.heatwave || rawData.mis_data.dr_heat_wave || [];
  }
  
  // ✅ Extract cumulative data separately
  let cumData = [];
  if (rawData && rawData.cumulative_data) {
    if (Array.isArray(rawData.cumulative_data)) {
      cumData = rawData.cumulative_data;
    } else if (rawData.cumulative_data.heatwave_cum) {
      cumData = rawData.cumulative_data.heatwave_cum;
    }
  } else if (rawData && rawData.mis_data && rawData.mis_data.heatwave_cum) {
    cumData = rawData.mis_data.heatwave_cum;
  }
  
  console.log(`- State data records: ${dataArray?.length || 0}`);
  console.log(`- Cumulative records: ${cumData?.length || 0}`);
  
  // ✅ NOW check if dataArray is valid
  if (!dataArray || !Array.isArray(dataArray) || dataArray.length === 0) {
    console.error('❌ No state heatwave data found');
    return { columns: [], transformedData: [] };
  }

  // ✅ CREATE CUMULATIVE LOOKUP MAP (by dist_id or dist_name)
  const cumMap = {};
  if (cumData && cumData.length > 0) {
    cumData.forEach(cumRec => {
      const key = cumRec.dist_id || cumRec.dist_name;
      cumMap[key] = cumRec;
    });
  }
  console.log(`✅ Built cumulative map with ${Object.keys(cumMap).length} entries`);

  const filteredData = dataArray.filter(row => {
    const distName = (row.dist_name || row.district_name || '').toString().trim().toUpperCase();
    return distName !== 'TOTAL';
  });

  const districtMap = {};

  filteredData.forEach(record => {
    const distKey = record.dist_name || record.district_name || 'Unknown';
    
    if (!districtMap[distKey]) {
      // ✅ Get cumulative data for this district
      const cumRec = cumMap[record.dist_id] || cumMap[distKey] || {};
      
      districtMap[distKey] = {
        dist_name: distKey,
        no_of_deaths_today: 0,
        no_of_deaths_cum: cumRec.no_of_deaths_cum || 0,
        no_of_dec_ex_gratia_pay_done_today: 0,
        no_of_dec_ex_gratia_pay_done_cum: cumRec.no_of_dec_ex_gratia_pay_done_cum || 0,
        no_of_persons_hospitalized_today: 0,
        no_of_persons_hospitalized_cum: cumRec.no_of_persons_hospitalized_cum || 0,
        no_of_persons_hospitalized_pay_done_today: 0,
        no_of_persons_hospitalized_pay_done_cum: cumRec.no_of_persons_hospitalized_pay_done_cum || 0,
        remarks: record.remarks || 'NA'
      };
    }

    const dist = districtMap[distKey];

    // Today values - SUM
    dist.no_of_deaths_today += (record.no_of_deaths_today || 0);
    dist.no_of_dec_ex_gratia_pay_done_today += (record.no_of_dec_ex_gratia_pay_done_today || 0);
    dist.no_of_persons_hospitalized_today += (record.no_of_persons_hospitalized_today || 0);
    dist.no_of_persons_hospitalized_pay_done_today += (record.no_of_persons_hospitalized_pay_done_today || 0);

    // ✅ Cum values - Get from cumulative data (don't use Math.max with records)
    // Already set in initialization from cumMap
  });

  const aggregatedDistricts = Object.values(districtMap);
  console.log(`✅ Aggregated into ${aggregatedDistricts.length} districts`);

  const fieldMapping = {
    'dist_name': 'District Name',
    'no_of_deaths_today': 'Number of Deaths due to heatwave (Today)',
    'no_of_deaths_cum': 'Number of Deaths (Cumulative)',
    'no_of_dec_ex_gratia_pay_done_today': 'Number of deceased person for whom ex-gratia payment done (Today)',
    'no_of_dec_ex_gratia_pay_done_cum': 'Number of deceased person for whom ex-gratia payment done (Cumulative)',
    'no_of_persons_hospitalized_today': 'Number of persons hospitalized (Today)',
    'no_of_persons_hospitalized_cum': 'Number of persons hospitalized (Cumulative)',
    'no_of_persons_hospitalized_pay_done_today': 'Number of hospitalized person for whom payment done (Today)',
    'no_of_persons_hospitalized_pay_done_cum': 'Number of hospitalized person for whom payment done (Cumulative)',
    'remarks': 'Remarks'
  };

  const excelColumnLabels = [
    "District Name",
    "Number of Deaths due to heatwave (Today)",
    "Number of Deaths (Cumulative)",
    "Number of deceased person for whom ex-gratia payment done (Today)",
    "Number of deceased person for whom ex-gratia payment done (Cumulative)",
    "Number of persons hospitalized (Today)",
    "Number of persons hospitalized (Cumulative)",
    "Number of hospitalized person for whom payment done (Today)",
    "Number of hospitalized person for whom payment done (Cumulative)",
    "Remarks"
  ];

  const columns = excelColumnLabels.map(label => ({ label, key: label }));

  const transformedData = aggregatedDistricts.map(row => {
    const transformedRow = {};
    Object.keys(fieldMapping).forEach(apiField => {
      const displayName = fieldMapping[apiField];
      const value = row[apiField];

      if (apiField.endsWith('_cum')) {
        // ✅ Show '-' only if value is 0, null, or undefined
        transformedRow[displayName] = (value !== null && value !== undefined && value !== 0) ? value : '-';
      } else if (apiField === 'dist_name') {
        transformedRow[displayName] = value || '';
      } else if (apiField === 'remarks') {
        // ✅ Show 'NA' for empty remarks
        transformedRow[displayName] = (value && value.trim() !== '' && value !== ',') ? value : 'NA';
      } else {
        transformedRow[displayName] = value;
      }
    });
    return transformedRow;
  });

  // Add TOTAL row
  const totalRow = { 'District Name': 'TOTAL' };

  excelColumnLabels.forEach((columnLabel, idx) => {
    if (idx === 0) return;
    
    if (columnLabel === 'Remarks') {
      totalRow[columnLabel] = 'NA';
      return;
    }

    let sum = 0;
    let validCount = 0;

    transformedData.forEach(row => {
      const value = row[columnLabel];
      if (value !== null && value !== undefined && value !== 'NA' && value !== '-' && value !== '' && !isNaN(value)) {
        sum += parseFloat(value);
        validCount++;
      }
    });

    totalRow[columnLabel] = validCount > 0 ? sum : 'NA';
  });

  transformedData.push(totalRow);

  return { columns, transformedData };
};


/**
 * Transform MIS State Datewise Heatwave Report
 */
/**
 * Transform MIS State Datewise Heatwave Report
 */
export const transformMISStateDatewiseData = (rawData) => {
  console.log('transformMISStateDatewiseData (Heatwave) called');
  
  if (!rawData || typeof rawData !== 'object') {
    console.error('❌ Invalid rawData');
    return { columns: [], transformedData: [] };
  }

  // ✅ HANDLE NESTED STRUCTURE - Extract array from object
  let datewiseData;
  if (Array.isArray(rawData)) {
    datewiseData = rawData;
  } else if (rawData.mis_data) {
    datewiseData = rawData.mis_data.heatwave || rawData.mis_data.dr_heat_wave || [];
  } else if (rawData.state_datewise) {
    datewiseData = rawData.state_datewise;
  } else if (rawData.data) {
    datewiseData = rawData.data;
  } else {
    datewiseData = [];
  }

  // ✅ Extract cumulative data
  let cumData = [];
  if (rawData.cumulative_data) {
    if (Array.isArray(rawData.cumulative_data)) {
      cumData = rawData.cumulative_data;
    } else if (rawData.cumulative_data.heatwave_cum) {
      cumData = rawData.cumulative_data.heatwave_cum;
    }
  } else if (rawData.mis_data && rawData.mis_data.heatwave_cum) {
    cumData = rawData.mis_data.heatwave_cum;
  }

  console.log(`- Datewise records: ${datewiseData?.length || 0}`);
  console.log(`- Cumulative records: ${cumData?.length || 0}`);

  if (!datewiseData || datewiseData.length === 0) {
    console.error('❌ No heatwave state datewise data found!');
    return { columns: [], transformedData: [] };
  }

  // ✅ CREATE CUMULATIVE LOOKUP MAP (by dist_id + reported_on)
  const cumMap = {};
  if (cumData && cumData.length > 0) {
    cumData.forEach(cumRec => {
      const key = `${cumRec.dist_id || cumRec.id}_${cumRec.reported_on}`;
      cumMap[key] = cumRec;
    });
  }
  console.log(`✅ Built cumulative map with ${Object.keys(cumMap).length} entries`);

  const fieldMapping = {
    'reported_on': 'Date',
    'dist_name': 'District Name',
    'no_of_deaths_today': 'Number of Deaths due to heatwave (Today)',
    'no_of_deaths_cum': 'Number of Deaths (Cumulative)',
    'no_of_dec_ex_gratia_pay_done_today': 'Number of deceased person for whom ex-gratia payment done (Today)',
    'no_of_dec_ex_gratia_pay_done_cum': 'Number of deceased person for whom ex-gratia payment done (Cumulative)',
    'no_of_persons_hospitalized_today': 'Number of persons hospitalized (Today)',
    'no_of_persons_hospitalized_cum': 'Number of persons hospitalized (Cumulative)',
    'no_of_persons_hospitalized_pay_done_today': 'Number of hospitalized person for whom payment done (Today)',
    'no_of_persons_hospitalized_pay_done_cum': 'Number of hospitalized person for whom payment done (Cumulative)',
    'remarks': 'Remarks'
  };

  const columns = Object.values(fieldMapping).map(label => ({ label, key: label }));

const transformedData = datewiseData.map(row => {
  const transformedRow = {};
  
  // ✅ Get matching cumulative record
  const cumKey = `${row.dist_id || row.id}_${row.reported_on}`;
  const cumRec = cumMap[cumKey] || {};
  
  Object.keys(fieldMapping).forEach(apiField => {
    const displayName = fieldMapping[apiField];
    
    if (apiField === 'reported_on') {
      transformedRow[displayName] = formatDateForMISExcel(row[apiField]);
    } else if (apiField.endsWith('_cum')) {
      // ✅ Get cumulative value from cumRec, not row
      const value = cumRec[apiField];
      transformedRow[displayName] = (value !== null && value !== undefined && value !== 0) ? value : '-';
    } else if (apiField === 'dist_name') {
      transformedRow[displayName] = row[apiField] || '';
    } else if (apiField === 'remarks') {  // ✅ ADD THIS BLOCK
      const value = row[apiField];
      transformedRow[displayName] = (value && value.trim() !== '' && value !== ',') ? value : 'NA';
    } else {
      const value = row[apiField];
      transformedRow[displayName] = (value !== null && value !== undefined) ? value : '';
    }
  });
  
  return transformedRow;
});


// ✅ Add TOTAL row
if (transformedData.length > 0) {
  const totalRow = {
    'Date': 'TOTAL',
    'District Name': 'NA'
  };

  Object.values(fieldMapping).forEach((columnLabel, idx) => {
    if (idx <= 1) return; // Skip Date and District Name
    
    // ✅ Handle Remarks column
    if (columnLabel === 'Remarks') {
      totalRow[columnLabel] = 'NA';
      return;
    }

    let sum = 0;
    let validCount = 0;

    transformedData.forEach(row => {
      const value = row[columnLabel];
      if (value !== null && value !== undefined && value !== 'NA' && value !== '-' && value !== '' && !isNaN(value)) {
        sum += parseFloat(value);
        validCount++;
      }
    });

    totalRow[columnLabel] = validCount > 0 ? sum : 'NA';
  });

  transformedData.push(totalRow);
}


  console.log(`✅ Transformed ${transformedData.length} state datewise rows for Excel`);
  return { columns, transformedData };
};
